import CheckmarkAnimationUrl from '@assets/checkmark-animation.gif';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { EventTypes } from '@racemap/utilities/consts/events';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import { Checkbox, Form, Modal } from 'antd';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';
import { type FC, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'src/lib/toast';
import type { CurrentEvent } from 'src/store/events/events_reducers';
import { Tristate } from '../utils/tristateTask';
import { buildDuplicateEventProps } from './buildDuplicateEventProps';
import { Footer } from './Footer';
import { NewEventNameField } from './NewEventNameField';
import { NewEventTimesField } from './NewEventTimesField';

type Props = {
  event: Immutable<CurrentEvent>;
};

export interface EventDuplicateFormFields {
  newEventName: string;
  newEventTimes: [DateTime, DateTime];
  withStarters: boolean;
  withGeoElements: boolean;
  withEditors: boolean;
  withCustomApps: boolean;
  copyToGroup: boolean;
}

const apiClient = RacemapAPIClient.fromWindowLocation();

export const EventDuplicateModal: FC<Props> = ({ event }) => {
  const [form] = Form.useForm<EventDuplicateFormFields>();
  const [savingState, setSavingState] = useState(Tristate.NORMAL);
  const [duplicatedEvent, setDuplicatedEvent] = useState<RacemapEvent | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const nextHourDate = DateTime.local().startOf('hour').plus({ hours: 1 });
  const show = location.pathname.endsWith('/duplicate');

  const handleClose = () => {
    setDuplicatedEvent(null);
    setSavingState(Tristate.NORMAL);
    navigate(`/admin/events/${event.id}/advanced`);
  };

  const handleCopyEvent = async ({
    newEventName,
    newEventTimes,
    withStarters,
    withGeoElements,
    withEditors,
    withCustomApps,
    copyToGroup,
  }: EventDuplicateFormFields) => {
    try {
      setSavingState(Tristate.SPINNING);

      setDuplicatedEvent(
        await apiClient.duplicateEvent(
          event.id,
          { withStarters, withGeoElements, withEditors, withCustomApps },
          buildDuplicateEventProps({
            event,
            newEventName,
            newEventTimes,
            copyToGroup,
          }),
        ),
      );
      setSavingState(Tristate.NORMAL);
    } catch (error) {
      toast.error(`Error duplicating event: ${(error as Error).message}`);
      setSavingState(Tristate.FAILURE);
    }
  };

  return (
    <Modal
      open={show}
      onCancel={handleClose}
      title={duplicatedEvent == null ? 'Duplicate Tracking Map' : 'Event successfully duplicated.'}
      footer={null}
    >
      <Form<EventDuplicateFormFields>
        onFinish={handleCopyEvent}
        layout="vertical"
        form={form}
        initialValues={{
          withStarters: false,
          withGeoElements: false,
          withEditors: false,
          withCustomApps: true,
          copyToGroup: false,
          newEventTimes: [nextHourDate, nextHourDate.plus({ hours: 1 })],
          newEventName: `${event.name || 'Untitled'} (Copy)`,
        }}
      >
        {duplicatedEvent == null ? (
          <>
            <NewEventNameField oldEventName={event.name || 'Untitled'} />
            <NewEventTimesField form={form} />

            <Form.Item name="withStarters" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>With participants</Checkbox>
            </Form.Item>

            <Form.Item name="withGeoElements" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>With tracks, POIs and splits</Checkbox>
            </Form.Item>

            {(event.type === EventTypes.CONTEST || event.type === EventTypes.STAGE) && (
              <Form.Item name="copyToGroup" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox>
                  Duplicate into current{' '}
                  {event.type === EventTypes.CONTEST ? 'group of single maps' : 'group of stages'}
                </Checkbox>
              </Form.Item>
            )}

            <Form.Item name="withEditors" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>With editors</Checkbox>
            </Form.Item>

            {event.modules.brandedAppIds.length > 0 && (
              <Form.Item name="withCustomApps" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox>With custom apps</Checkbox>
              </Form.Item>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src={CheckmarkAnimationUrl}
              alt="Checkmark animation"
              style={{ width: 200, height: 200 }}
            />
          </div>
        )}
        <Footer onClose={handleClose} duplicatedEvent={duplicatedEvent} savingState={savingState} />
      </Form>
    </Modal>
  );
};
